<script>
import { GlButton, GlTooltipDirective, GlAnimatedTodoIcon } from '@gitlab/ui';
import { produce } from 'immer';
import { createAlert } from '~/alert';
import { TYPE_MERGE_REQUEST } from '~/issues/constants';
import { __, sprintf } from '~/locale';
import Tracking from '~/tracking';
import { todoMutationTypes } from '../../constants';
import { todoQueries, todoMutations } from '../../queries/constants';
import { todoLabel } from '../../utils';
import TodoButton from './todo_button.vue';

const trackingMixin = Tracking.mixin();

export default {
  name: 'SidebarTodoWidget',
  components: {
    GlButton,
    TodoButton,
    GlAnimatedTodoIcon,
  },
  directives: {
    GlTooltip: GlTooltipDirective,
  },
  mixins: [trackingMixin],
  inject: {
    isClassicSidebar: {
      default: false,
    },
  },
  props: {
    issuableId: {
      type: String,
      required: true,
    },
    issuableIid: {
      type: String,
      required: true,
    },
    fullPath: {
      type: String,
      required: true,
    },
    issuableType: {
      required: true,
      type: String,
    },
  },
  emits: ['todoUpdated'],
  data() {
    return {
      todoId: null,
      todoCount: 0,
      loading: false,
    };
  },
  apollo: {
    todoId: {
      query() {
        return todoQueries[this.issuableType].query;
      },
      variables() {
        return {
          fullPath: this.fullPath,
          iid: String(this.issuableIid),
        };
      },
      skip() {
        return !this.issuableIid;
      },
      update(data) {
        return data.namespace?.issuable?.currentUserTodos.nodes[0]?.id;
      },
      result({ data }) {
        if (!data) {
          return;
        }

        const currentUserTodos = data.namespace?.issuable?.currentUserTodos?.nodes ?? [];
        this.todoId = currentUserTodos[0]?.id;
        this.todoCount = currentUserTodos.length;
        this.$emit('todoUpdated', currentUserTodos.length > 0);
      },
      error() {
        createAlert({
          message: sprintf(__('Something went wrong while setting %{issuableType} to-do item.'), {
            issuableType: this.issuableType,
          }),
        });
      },
      subscribeToMore: {
        document() {
          return todoQueries[this.issuableType].subscription;
        },
        variables() {
          return {
            issuableId: this.issuableId,
          };
        },
        skip() {
          return !todoQueries[this.issuableType].subscription;
        },
      },
    },
  },
  computed: {
    isMergeRequest() {
      return this.issuableType === TYPE_MERGE_REQUEST;
    },
    todoIdQuery() {
      return todoQueries[this.issuableType].query;
    },
    todoIdQueryVariables() {
      return {
        fullPath: this.fullPath,
        iid: String(this.issuableIid),
      };
    },
    isLoading() {
      return this.$apollo.queries?.todoId?.loading || this.loading;
    },
    hasTodo() {
      return Boolean(this.todoId);
    },
    todoMutationType() {
      if (this.hasTodo) {
        return todoMutationTypes.markDone;
      }
      return todoMutationTypes.create;
    },
    tootltipTitle() {
      return todoLabel(this.hasTodo);
    },
    hasTodoStateText() {
      return this.hasTodo ? 'true' : 'false';
    },
  },
  methods: {
    toggleTodo() {
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: todoMutations[this.todoMutationType],
          variables: {
            input: {
              targetId: this.issuableId,
            },
          },
          update: (
            store,
            {
              data: {
                todoMutation: { todo, todos },
              },
            },
          ) => {
            const queryProps = {
              query: this.todoIdQuery,
              variables: this.todoIdQueryVariables,
            };
            const sourceData = store.readQuery(queryProps);
            const data = produce(sourceData, (draftState) => {
              draftState.namespace.issuable.currentUserTodos.nodes =
                todos !== undefined ? [] : [todo];
            });
            store.writeQuery({
              data,
              ...queryProps,
            });
          },
        })
        .then(
          ({
            data: {
              todoMutation: { errors },
            },
          }) => {
            if (errors.length) {
              createAlert({
                message: errors[0],
              });
            }
            this.track('click_todo', {
              label: 'right_sidebar',
              property: this.hasTodo,
            });
          },
        )
        .catch(() => {
          createAlert({
            message: sprintf(__('Something went wrong while setting %{issuableType} to-do item.'), {
              issuableType: this.issuableType,
            }),
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<template>
  <div data-testid="sidebar-todo" :class="{ 'inline-block': !isMergeRequest }">
    <todo-button
      v-gl-tooltip.hover.top
      :title="tootltipTitle"
      :issuable-type="issuableType"
      :issuable-id="issuableId"
      :is-todo="hasTodo"
      :todo-count="todoCount"
      :disabled="isLoading"
      :is-icon-button="true"
      :aria-pressed="hasTodoStateText"
      :selected="hasTodo"
      class="hide-collapsed !gl-align-top"
      @click.stop.prevent="toggleTodo"
    >
      <gl-animated-todo-icon :is-on="hasTodo" class="gl-button-icon" />
    </todo-button>
    <gl-button
      v-if="isClassicSidebar && !isMergeRequest"
      v-gl-tooltip.left.viewport
      :title="tootltipTitle"
      category="tertiary"
      type="reset"
      class="sidebar-collapsed-icon sidebar-collapsed-container !gl-rounded-none !gl-shadow-none"
      @click.stop.prevent="toggleTodo"
    >
      <gl-animated-todo-icon
        :is-on="hasTodo"
        :class="{ '!gl-text-status-info': hasTodo }"
        class="gl-button-icon"
      />
    </gl-button>
  </div>
</template>
